import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'
import HomeView from '../views/home/HomeView.vue'
import DashboardView from '../views/dashboard/DashboardView.vue'
import ProductView from '../views/home/ProductView.vue'
import DashboardLayout from '../components/DashboardLayout.vue'
import TeamInviteView from '../views/home/TeamInviteView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/team-invite/:id',
      name: 'team-invite',
      component: TeamInviteView,
      props: true
    },
    {
      path: '/channels/invites/accept',
      name: 'channel-invite-accept',
      component: () => import('../views/home/ChannelInviteAcceptView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/channels/:channelId/open',
      name: 'channel-open',
      component: () => import('../views/home/ChannelOpenView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/dashboard',
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: DashboardView,
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('../views/dashboard/UsersView.vue'),
        },
        {
          path: 'groups',
          name: 'groups',
          component: () => import('../views/dashboard/GroupsView.vue'),
        },
        {
          path: 'invites',
          name: 'invites',
          component: () => import('../views/dashboard/InvitesView.vue'),
        },
        {
          path: 'channels',
          name: 'channels',
          component: () => import('../views/dashboard/ChannelsView.vue'),
        },
        {
          path: 'roles',
          name: 'roles',
          component: () => import('../views/dashboard/RolesView.vue'),
        },
        {
          path: 'triggers',
          name: 'triggers',
          component: () => import('../views/dashboard/TriggersView.vue'),
        },
        {
          path: 'workflows',
          name: 'workflows',
          component: () => import('../views/dashboard/WorkflowsView.vue'),
        },
        {
          path: 'projects',
          name: 'projects',
          component: () => import('../views/dashboard/ProjectsView.vue'),
        },
        {
          path: 'git-keys',
          name: 'git-keys',
          component: () => import('../views/dashboard/GitKeysView.vue'),
        },
        {
          path: 'ssh-keys',
          name: 'ssh-keys',
          component: () => import('../views/dashboard/SSHKeysView.vue'),
        },
        {
          path: 'environments',
          name: 'environments',
          component: () => import('../views/dashboard/EnvironmentsView.vue'),
        },
        {
          path: 'logs',
          name: 'logs',
          component: () => import('../views/dashboard/LogsView.vue'),
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('../views/dashboard/HelpView.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('../views/dashboard/SettingsView.vue'),
        },
        {
          path: 'compute-profiles',
          name: 'compute-profiles',
          component: () => import('../views/dashboard/ComputeProfilesView.vue'),
        },
        {
          path: 'auth-tokens',
          name: 'auth-tokens',
          component: () => import('../views/dashboard/AuthTokensView.vue'),
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import('../views/dashboard/BillingView.vue'),
        },
        {
          path: 'models',
          name: 'models',
          component: () => import('../views/dashboard/ModelsView.vue'),
        },
        {
          path: 'marketplace',
          name: 'marketplace',
          component: () => import('../views/dashboard/MarketplaceView.vue'),
        },
        {
          path: 'oauth-callback',
          name: 'oauth-callback',
          component: () => import('../views/dashboard/OAuthCallbackView.vue'),
        },
        {
          path: 'github-callback',
          name: 'github-callback',
          redirect: to => ({
            path: '/dashboard/oauth-callback',
            query: to.query,
            hash: to.hash,
          }),
        },
      ]
    },
    {
      path: '/admin',
      component: () => import('../components/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdminMode: true },
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: () => import('../views/admin/TopAccountsAdminView.vue'),
        },
        {
          path: 'accounts',
          name: 'admin-accounts',
          component: () => import('../views/admin/AccountsAdminView.vue'),
        },
        {
          path: 'static-ips',
          name: 'admin-static-ips',
          component: () => import('../views/admin/StaticIpsAdminView.vue'),
        },
        {
          path: 'environments',
          name: 'admin-environments',
          component: () => import('../views/admin/EnvironmentsAdminView.vue'),
        },
        {
          path: 'job-schedules',
          name: 'admin-job-schedules',
          component: () => import('../views/admin/JobSchedulesAdminView.vue'),
        },
        {
          path: 'model-pricing',
          name: 'admin-model-pricing',
          component: () => import('../views/admin/ModelPricingAdminView.vue'),
        },
        {
          path: 'provider-integrations',
          name: 'admin-provider-integrations',
          component: () => import('../views/dashboard/ProviderIntegrationsView.vue'),
        }
      ]
    },
    {
      path: '/oauth/callback',
      name: 'oauth-callback-relay',
      component: () => import('../views/home/OAuthCallbackRelayView.vue'),
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('../views/home/AuthCallbackView.vue'),
    },
    {
      path: '/auth/callback/:provider',
      name: 'auth-callback-provider',
      component: () => import('../views/home/AuthCallbackView.vue'),
      props: true
    },
    {
      path: '/auth/callback/:provider/:action',
      name: 'auth-callback-provider-action',
      component: () => import('../views/home/AuthCallbackView.vue'),
      props: true
    },
    {
      path: '/auth/callback/:provider/:action/:subaction',
      name: 'auth-callback-provider-action-subaction',
      component: () => import('../views/home/AuthCallbackView.vue'),
      props: true
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: HomeView,
      props: true,
    },
    {
      path: '/authorize',
      name: 'authorize',
      component: () => import('../views/home/AuthorizeView.vue'),
      props: (route) => ({
        payload: route.query.payload,
        signature: route.query.signature
      })
    },
    {
      path: '/about',
      name: 'about',
      component: ProductView,
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('../views/home/PricingView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/features',
      name: 'features',
      component: () => import('../views/home/FeaturesView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/platform',
      name: 'platform',
      component: () => import('../views/home/PlatformView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/waitlist',
      name: 'waitlist',
      component: () => import('../views/home/WaitListView.vue'),
    },
    {
      path: '/account-setup',
      name: 'account-setup',
      component: () => import('../views/home/AccountSetupView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/signin',
      name: 'signin',
      component: () => import('../views/home/SignInView.vue'),
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/home/TermsOfServiceView.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/home/PrivacyPolicyView.vue'),
    },
    {
      path: '/pay/:id',
      name: 'payment',
      component: () => import('../views/home/PaymentView.vue'),
      props: true
    },
  ],
})

// Route persistence functions
const saveCurrentRoute = (route: any) => {
  const isDashboardOrAdmin = route.path.startsWith('/dashboard') || route.path.startsWith('/admin')
  const isAuthCallback = route.path.includes('/auth/callback')

  console.log('saveCurrentRoute called with:', {
    path: route.path,
    fullPath: route.fullPath,
    shouldSave: isDashboardOrAdmin && !isAuthCallback
  })

  if (isDashboardOrAdmin && !isAuthCallback) {
    console.log('Saving protected route:', route.fullPath)
    localStorage.setItem('last_dashboard_route', route.fullPath)
    sessionStorage.setItem('should_restore_dashboard', 'true')
  }
}

const getLastDashboardRoute = (): string | null => {
  const lastRoute = localStorage.getItem('last_dashboard_route')
  console.log('getLastDashboardRoute retrieved:', lastRoute)
  return lastRoute || null
}

const clearLastDashboardRoute = () => {
  localStorage.removeItem('last_dashboard_route')
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.initialized) {
    await authStore.initializeAuth()
  }

  if (!authStore.session) {
    const { data } = await supabase.auth.getSession()
    if (data.session) {
      authStore.session = data.session
    }
  }

  console.log('Navigation guard running:', {
    from: from.path,
    to: to.path,
    shouldClearFlag: !to.path.startsWith('/dashboard') && from.path.startsWith('/dashboard'),
    flagExists: !!sessionStorage.getItem('should_restore_dashboard')
  })

  const isAppModeAdmin = import.meta.env.VITE_APP_MODE === 'admin'

  // MODE SWAPPING LOGIC: Prevent cross-contamination between Public and Admin apps
  if (to.path.startsWith('/dashboard') && isAppModeAdmin) {
    console.log('Admin Mode active: Redirecting dashboard attempt to /admin')
    return next('/admin')
  }
  if (to.path.startsWith('/admin') && !isAppModeAdmin) {
    console.log('Standard Mode active: Blocking /admin attempt')
    return next('/dashboard')
  }

  // Clear restoration flag when navigating away from protected UI
  const isLeavingProtected = (!to.path.startsWith('/dashboard') && !to.path.startsWith('/admin')) &&
                             (from.path.startsWith('/dashboard') || from.path.startsWith('/admin'));

  if (isLeavingProtected) {
    console.log('Clearing restoration flag')
    sessionStorage.removeItem('should_restore_dashboard')
  }
  
  // Save protected routes for restoration (but not during auth flows)
  if (!to.path.startsWith('/auth/') && !to.path.startsWith('/team-invite/') && !to.path.startsWith('/channels/invites/accept')) {
    saveCurrentRoute(to)
  }
  
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/')
  } else if ((to.path.startsWith('/dashboard/') || to.path.startsWith('/admin/')) && authStore.isAuthenticated) {
    // Approved users without an account should continue to account setup, not waitlist.
    if (authStore.accountUsers.length === 0) {
      next('/account-setup')
    } else {
      next()
    }
  } else if ((to.path === '/dashboard' || to.path === '/admin') && authStore.isAuthenticated) {
    // Special case for protected roots - approved users without an account should go to setup.
    if (!isAppModeAdmin && authStore.accountUsers.length === 0) {
      next('/account-setup')
    } else {
      next()
    }
  } else if (to.path.startsWith('/team-invite/')) {
    // Allow team invite pages to be accessed regardless of auth state
    next()
  } else {
    next()
  }
})

export default router

// Export route persistence functions
export { saveCurrentRoute, getLastDashboardRoute, clearLastDashboardRoute }